// src/components/dashboard/RegistrationForm.jsx
import React, { useState, useEffect, useRef } from 'react';
import { X, Calendar, Phone, Truck, User, MapPin, AlertTriangle, AlertCircle } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import useAuth from '../../hooks/useAuth';
import { api } from '../../services/api';
import { nowLocalInput } from '../../utils/time';
import SearchableSelect from '../adminpanel/SearchableSelect';

const RegistrationForm = ({ isOpen, onClose, onSubmit, initialData, vehicleData }) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [truckBlocked, setTruckBlocked] = useState(false);
  const checkTimeout = useRef(null);
  const [vehicles, setVehicles] = useState([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [truckQuery, setTruckQuery] = useState('');
  
  const [formData, setFormData] = useState({
  company: initialData?.company || user?.username || '',
  number: initialData?.number || '',
  phone: initialData?.phone || vehicleData?.driver_phone || '',
  name: initialData?.name || vehicleData?.driver_name || '',
  trailer_number: initialData?.trailer_number || '',
  transit_in: initialData?.transit_in || 0,
  comment: initialData?.comment || '',
  entry_point: initialData?.entry_point || '',
  exit_point: initialData?.exit_point || '',
  parking_40: initialData?.parking_40 || false,
  status: initialData?.status || 'on_road',
  departure_date: initialData?.departure_date || nowLocalInput(),
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        company: initialData.company || '',
        number: initialData.number || '',
        phone: initialData.phone || vehicleData?.driver_phone || '',
        name: initialData.name || vehicleData?.driver_name || '',
        trailer_number: initialData.trailer_number || '',
        transit_in: initialData.transit_in || 0,
        comment: initialData.comment || '',
        entry_point: initialData.entry_point || '',
        exit_point: initialData.exit_point || '',
		parking_40: initialData.parking_40 || false,
        status: initialData.status || 'on_road',
        departure_date: initialData.departure_date || nowLocalInput(),
      });
    } else {
      setFormData({
        company: user?.username || '',
        number: '',
        phone: vehicleData?.driver_phone || '',
        name: vehicleData?.driver_name || '',
        trailer_number: '',
        transit_in: 0,
        comment: '',
        entry_point: '',
        exit_point: '',
		parking_40: false,
        status: 'on_road',
        departure_date: nowLocalInput(),
      });
    }
  }, [initialData, user, vehicleData]);

  // Load the user's own fleet for the vehicle dropdowns.
  useEffect(() => {
    if (!isOpen) return undefined;
    let active = true;
    setVehiclesLoading(true);
    api.getVehicles()
      .then(data => { if (active) setVehicles(Array.isArray(data) ? data : (data?.results || [])); })
      .catch(() => { if (active) setVehicles([]); })
      .finally(() => { if (active) setVehiclesLoading(false); });
    return () => { active = false; };
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const isTruck   = (v) => ['truck', 'tractor'].includes(v.type);
  const isTrailer = (v) => v.type === 'semi_trailer';

  const selectTruck = (plate) => {
    const veh = vehicles.find(v => v.plate_number === plate && isTruck(v));
    setFormData(prev => ({
      ...prev,
      number: plate,
      name:  veh?.driver_name || prev.name,
      phone: veh?.driver_phonenumber || prev.phone,
    }));
    setTruckBlocked(false);
    clearTimeout(checkTimeout.current);
    if (plate) checkTruck(plate);
  };

  const selectTrailer = (plate) => setFormData(prev => ({ ...prev, trailer_number: plate }));

  const checkTruck = async (number) => {
    try {
      const excludeId = initialData?.id || '';
      const res = await api.get(`/registrations/check-truck/?number=${encodeURIComponent(number)}${excludeId ? `&exclude_id=${excludeId}` : ''}`);
      setTruckBlocked(res.data.blocked);
    } catch {
      setTruckBlocked(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
	if (formData.number === '') {
		alert(t('fillNumber'));
		return;
	}
    if (truckBlocked) {
      alert('Эта машина уже в пути (on_road). Новая регистрация невозможна.');
      return;
    }
	  if (formData.entry_point === '' && formData.exit_point === '') {
    alert(t('selectEntryExitPoints'));
    return;
  }
    else if (formData.entry_point === '') {
    alert(t('selectEntryPoint'));
    return;
  }
    else if (formData.exit_point === '') {
    alert(t('selectExitPoint'));
    return;
  }
    const submitData = { 
      ...formData,
      transit_in: parseInt(formData.transit_in) || 0,
    };
    
    
    onSubmit(submitData);
  };

  if (!isOpen) return null;

  const truckList = vehicles.filter(isTruck);
  const trailerList = vehicles.filter(isTrailer);
  let truckOptions = truckList.map(v => ({ value: v.plate_number, label: v.plate_number }));
  // Keep the current plate selectable when editing, even if not in the active fleet.
  if (formData.number && !truckOptions.some(o => o.value === formData.number)) {
    truckOptions = [{ value: formData.number, label: formData.number }, ...truckOptions];
  }
  const trailerOptions = trailerList.map(v => ({ value: v.plate_number, label: v.plate_number }));
  const locked = !formData.number;
  const truckSearchNoMatch = !!truckQuery &&
    !truckOptions.some(o => String(o.label).toLowerCase().includes(truckQuery.toLowerCase()));
  const showNoTruckNote = !vehiclesLoading && (truckList.length === 0 || truckSearchNoMatch);

  const entryPoints = [
    { value: '', label: t('select') },
    { value: 'daryal', label: t('daryal') },
	{value: 'no_need', label: t('no_need')}
  ];
    const exitPoints = [
    { value: '', label: t('select') },
    { value: 'bagratashen', label: t('bagratashen') },
    { value: 'gogavan', label: t('gogavan') },
	{value: 'no_need', label: t('no_need')}
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 bg-gradient-to-r from-slate-800 to-slate-700 text-white">
          <h2 className="text-xl font-semibold">
            {initialData ? t('editRegistration') : t('register')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-white hover:text-gray-200 rounded-full transition duration-150"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className={`block text-sm font-medium mb-1 ${formData.number === '' ? 'text-red-600' : 'text-gray-700'}`}>
                    {t('truckNumber')} *
                  </label>
                  {showNoTruckNote && (
                    <p className="mb-1 text-xs text-amber-600 flex items-center gap-1">
                      <AlertCircle className="h-3.5 w-3.5" />
                      {truckList.length === 0 ? t('noVehiclesFound') : t('vehicleNotInFleet')}
                    </p>
                  )}
                  <SearchableSelect
                    icon={<Truck className={`h-5 w-5 ${formData.number === '' ? 'text-red-500' : 'text-gray-400'}`} />}
                    placeholder={`— ${t('select')} —`}
                    value={formData.number}
                    options={truckOptions}
                    onChange={selectTruck}
                    emptyLabel={truckQuery ? t('vehicleNotInFleet') : t('noVehiclesFound')}
                    onQueryChange={setTruckQuery}
                  />
                  {truckBlocked && (
                    <div className="mt-1 flex items-center gap-1 text-sm text-red-600">
                      <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                      <span>Эта машина уже в пути (on_road). Регистрация заблокирована.</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Everything below is locked until a vehicle is selected */}
            <fieldset disabled={locked} className="disabled:opacity-60">
            <div className="mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('trailerNumber')}
                  </label>
                  <SearchableSelect
                    icon={<Truck className="h-5 w-5 text-gray-400" />}
                    placeholder={`— ${t('select')} —`}
                    value={formData.trailer_number}
                    options={trailerOptions}
                    onChange={selectTrailer}
                    emptyLabel={t('noVehiclesFound')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('transitIn')}
                  </label>
                  <input
                    type="number"
                    name="transit_in"
                    value={formData.transit_in}
                    onChange={handleChange}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>


                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('comment')}
                  </label>
                  <textarea
                    name="comment"
                    value={formData.comment}
                    onChange={handleChange}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-800 mb-4 pb-2 border-b border-gray-200">
                {t('driverInfo')}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('name')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('phone')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Phone className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-800 mb-4 pb-2 border-b border-gray-200">
                {t('routeInfo')}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium  ${formData.entry_point === '' ? 'text-red-600' : 'text-gray-700'}`}>
                    {t('entryPoint')}*
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MapPin className={`h-5 w-5 ${formData.entry_point === '' ? 'text-red-500' : 'text-gray-400'}`} />
                    </div>
                    <select
                      name="entry_point"
                      value={formData.entry_point}
                      onChange={handleChange}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      {entryPoints.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className={`block text-sm font-medium  ${formData.exit_point === '' ? 'text-red-600' : 'text-gray-700'}`}>
                    {t('exitPoint')}*
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MapPin className={`h-5 w-5 ${formData.exit_point === '' ? 'text-red-500' : 'text-gray-400'}`} />
                    </div>
                    <select
                      name="exit_point"
                      value={formData.exit_point}
                      onChange={handleChange}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      {exitPoints.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
            {/* Parking 40 Nataxhtari */}
              <div className="mb-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="parking_40"
                    name="parking_40"
                    checked={formData.parking_40}
                    onChange={handleChange}
                    disabled={true}
                    className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="parking_40" className="ml-2 block text-sm text-gray-700 font-medium">
                    {t('parking_40')} Coming soon
                  </label>
                </div>
              </div>
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-800 mb-4 pb-2 border-b border-gray-200">
                {t('statusAndDate')}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('status')}
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
					disabled={true}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="on_road">{t('onRoad')}</option>
                    <option value="completed">{t('completed')}</option>
                    <option value="cancelled">{t('cancelled')}</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('departureDate')} *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Calendar className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="datetime-local"
                      name="departure_date"
                      value={formData.departure_date}
                      onChange={handleChange}
                      required
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            </fieldset>

            <input type="hidden" name="company" value={formData.company} />
          </form>
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
          >
            {t('cancel')}
          </button>
          
          <button
            type="button"
            onClick={handleSubmit}
            disabled={truckBlocked || locked}
            className={`px-4 py-2 text-white rounded-md transition-colors ${(truckBlocked || locked) ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary-600 hover:bg-primary-700'}`}
          >
            {initialData ? t('update') : t('save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RegistrationForm;